import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Tabs,
  Tab,
  CircularProgress,
  Alert,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
} from '@mui/material';
import {
  Support as SupportIcon,
  Category as CategoryIcon,
  Assessment as AnalyticsIcon,
  CheckCircle as ResolveIcon,
  Schedule as PendingIcon,
  Cancel as CloseIcon,
  Reply as ReplyIcon,
} from '@mui/icons-material';
import { useUnifiedTranslation } from '../../hooks/useUnifiedTranslation';
import { supportApi } from '../../services/api/support.api';
import { DataTable } from '../ui/DataTable';
import { formatShortDate } from '../../utils/date-utils';
import { PAGE_SIZE } from '../../constants/pagination';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`support-tabpanel-${index}`}
      aria-labelledby={`support-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ py: 3 }}>{children}</Box>}
    </div>
  );
}

export const SupportAgentDashboard: React.FC = () => {
  const { t } = useUnifiedTranslation({ namespace: 'dashboard' });
  const ts = (key: string, fallback: string): string => { const v = t(key); return typeof v === 'string' ? v : fallback; };
  const queryClient = useQueryClient();
  const [activeTab, setActiveTab] = useState(0);
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedTicketId, setSelectedTicketId] = useState<string>('');

  // Загрузка тикетов
  const { data: ticketsData, isLoading: isLoadingTickets } = useQuery({
    queryKey: ['support', 'tickets'],
    queryFn: async () => {
      const response = await supportApi.getTickets({ limit: PAGE_SIZE.LARGE });
      return response.data;
    },
    refetchInterval: 30000,
  });

  // Загрузка категорий
  const { data: categoriesData, isLoading: isLoadingCategories } = useQuery({
    queryKey: ['support', 'categories'],
    queryFn: async () => {
      const response = await supportApi.getCategories();
      return response.data;
    },
  });

  // Загрузка аналитики
  const { data: analyticsData, isLoading: isLoadingAnalytics } = useQuery({
    queryKey: ['support', 'analytics'],
    queryFn: async () => {
      const response = await supportApi.getAnalytics();
      return response.data;
    },
  });

  // Загрузка статистики
  const { data: statsData } = useQuery({
    queryKey: ['support', 'stats'],
    queryFn: async () => {
      const response = await supportApi.getAnalyticsStats();
      return response.data;
    },
    refetchInterval: 60000,
  });

  // Мутация обновления тикета
  const updateTicketMutation = useMutation({
    mutationFn: ({ ticketId, data }: { ticketId: string; data: any }) =>
      supportApi.updateTicket(ticketId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support'] });
      alert('Тикет обновлен!');
    },
  });

  // Мутация добавления ответа
  const addReplyMutation = useMutation({
    mutationFn: ({ ticketId, content }: { ticketId: string; content: string }) =>
      supportApi.addReply(ticketId, content),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['support'] });
      setReplyDialogOpen(false);
      alert('Ответ добавлен!');
    },
  });

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue);
  };

  const handleTicketStatusChange = (ticketId: string, status: string) => {
    updateTicketMutation.mutate({ ticketId, data: { status } });
  };

  const handleOpenReply = (ticketId: string) => {
    setSelectedTicketId(ticketId);
    setReplyDialogOpen(true);
  };

  const handleSendReply = () => {
    const content = (document.getElementById('reply-content') as HTMLTextAreaElement)?.value;
    if (content && selectedTicketId) {
      addReplyMutation.mutate({ ticketId: selectedTicketId, content });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Заголовок */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          {ts('supportAgent.title', 'Панель службы поддержки')}
        </Typography>
        <Typography variant="body1" color="text.secondary">
          {ts('supportAgent.subtitle', 'Управление обращениями пользователей')}
        </Typography>
      </Box>

      {/* KPI карточки */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('supportAgent.kpis.activeTickets', 'Активные тикеты')}
                  </Typography>
                  <Typography variant="h4">
                    {statsData?.openTickets || 0}
                  </Typography>
                </Box>
                <PendingIcon color="warning" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('supportAgent.kpis.resolvedTickets', 'Решено')}
                  </Typography>
                  <Typography variant="h4">
                    {statsData?.resolvedTickets || 0}
                  </Typography>
                </Box>
                <ResolveIcon color="success" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('supportAgent.kpis.avgResponseTime', 'Ср. время ответа')}
                  </Typography>
                  <Typography variant="h4">
                    {Math.round((statsData?.averageResponseTime || 0) / 60)} мин
                  </Typography>
                </Box>
                <SupportIcon color="info" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Box>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    {ts('supportAgent.kpis.satisfactionRate', 'Удовлетворенность')}
                  </Typography>
                  <Typography variant="h4">
                    {((statsData?.satisfactionRate || 0) * 100).toFixed(0)}%
                  </Typography>
                </Box>
                <AnalyticsIcon color="primary" sx={{ fontSize: 40 }} />
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Вкладки */}
      <Card>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            <Tab label={ts('supportAgent.tabs.tickets', 'Тикеты')} icon={<SupportIcon />} iconPosition="start" />
            <Tab label={ts('supportAgent.tabs.categories', 'Категории')} icon={<CategoryIcon />} iconPosition="start" />
            <Tab label={ts('supportAgent.tabs.analytics', 'Аналитика')} icon={<AnalyticsIcon />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Вкладка: Тикеты */}
        <TabPanel value={activeTab} index={0}>
          {isLoadingTickets ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : ticketsData?.tickets && ticketsData.tickets.length > 0 ? (
            <DataTable
              title="Обращения пользователей"
              columns={[
                {
                  id: 'title',
                  label: 'Тема',
                  sortable: true,
                },
                {
                  id: 'userName',
                  label: 'Пользователь',
                },
                {
                  id: 'categoryName',
                  label: 'Категория',
                  render: (row: any) => row.categoryName || '—',
                },
                {
                  id: 'priority',
                  label: 'Приоритет',
                  render: (row: any) => (
                    <Chip
                      label={row.priority}
                      size="small"
                      color={
                        row.priority === 'critical' ? 'error' :
                        row.priority === 'high' ? 'warning' :
                        row.priority === 'medium' ? 'info' : 'default'
                      }
                    />
                  ),
                },
                {
                  id: 'status',
                  label: 'Статус',
                  render: (row: any) => (
                    <Chip
                      label={row.status}
                      size="small"
                      color={
                        row.status === 'resolved' ? 'success' :
                        row.status === 'in_progress' ? 'info' :
                        row.status === 'closed' ? 'default' : 'warning'
                      }
                    />
                  ),
                },
                {
                  id: 'createdAt',
                  label: 'Создан',
                  render: (row: any) => formatShortDate(row.createdAt),
                },
                {
                  id: 'actions',
                  label: 'Действия',
                  render: (row: any) => (
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <IconButton
                        size="small"
                        color="primary"
                        onClick={() => handleOpenReply(row.id)}
                        title="Ответить"
                      >
                        <ReplyIcon />
                      </IconButton>
                      {row.status === 'in_progress' && (
                        <IconButton
                          size="small"
                          color="success"
                          onClick={() => handleTicketStatusChange(row.id, 'resolved')}
                          title="Решено"
                        >
                          <ResolveIcon />
                        </IconButton>
                      )}
                      {row.status !== 'closed' && (
                        <IconButton
                          size="small"
                          onClick={() => handleTicketStatusChange(row.id, 'closed')}
                          title="Закрыть"
                        >
                          <CloseIcon />
                        </IconButton>
                      )}
                    </Box>
                  ),
                },
              ]}
              data={ticketsData.tickets}
              rowKey={(row: any) => row.id}
              pagination
              searchable
            />
          ) : (
            <Alert severity="info">
              Нет тикетов
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Категории */}
        <TabPanel value={activeTab} index={1}>
          {isLoadingCategories ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : categoriesData && categoriesData.length > 0 ? (
            <Grid container spacing={2}>
              {categoriesData.map((category: any) => (
                <Grid item xs={12} md={6} key={category.id}>
                  <Card>
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <Box>
                          <Typography variant="h6">{category.name}</Typography>
                          <Typography variant="body2" color="text.secondary">
                            {category.description || 'Без описания'}
                          </Typography>
                          <Chip 
                            label={`${category.ticketCount || 0} тикетов`} 
                            size="small" 
                            sx={{ mt: 1 }} 
                          />
                        </Box>
                        <Chip
                          label={category.isActive ? 'Активна' : 'Неактивна'}
                          size="small"
                          color={category.isActive ? 'success' : 'default'}
                        />
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          ) : (
            <Alert severity="info">
              Нет категорий
            </Alert>
          )}
        </TabPanel>

        {/* Вкладка: Аналитика */}
        <TabPanel value={activeTab} index={2}>
          {isLoadingAnalytics ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
              <CircularProgress />
            </Box>
          ) : analyticsData ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Производительность
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Ср. время ответа:</Typography>
                        <Typography fontWeight="bold">
                          {Math.round((analyticsData.stats?.averageResponseTime || 0) / 60)} мин
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography>Ср. время решения:</Typography>
                        <Typography fontWeight="bold">
                          {Math.round((analyticsData.stats?.averageResolutionTime || 0) / 3600)} ч
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography>Удовлетворенность:</Typography>
                        <Typography fontWeight="bold" color="success.main">
                          {((analyticsData.stats?.satisfactionRate || 0) * 100).toFixed(1)}%
                        </Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>

              <Grid item xs={12} md={6}>
                <Card>
                  <CardContent>
                    <Typography variant="h6" gutterBottom>
                      Распределение по приоритетам
                    </Typography>
                    <Box sx={{ mt: 2 }}>
                      {analyticsData.trends?.ticketsByPriority?.map((item: any, index: number) => (
                        <Box key={index} sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                          <Chip 
                            label={item.priority} 
                            size="small"
                            color={
                              item.priority === 'critical' ? 'error' :
                              item.priority === 'high' ? 'warning' : 'default'
                            }
                          />
                          <Typography fontWeight="bold">{item.count}</Typography>
                        </Box>
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          ) : (
            <Alert severity="info">
              Нет данных аналитики
            </Alert>
          )}
        </TabPanel>
      </Card>

      {/* Диалог добавления ответа */}
      <Dialog 
        open={replyDialogOpen} 
        onClose={() => setReplyDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Ответить на тикет</DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 2 }}>
            <TextField
              fullWidth
              label="Ответ"
              multiline
              rows={6}
              id="reply-content"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReplyDialogOpen(false)}>
            Отмена
          </Button>
          <Button variant="contained" onClick={handleSendReply}>
            Отправить
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SupportAgentDashboard;

